import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../lib/axios';
import { trackUserRegistration, trackConsentAcceptance } from '../services/analytics';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';

export default function VisitorRegistrationPage() {
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [tenantId, setTenantId] = useState<string>('');
  const [tenantName, setTenantName] = useState<string>('');
  const [tenantSlug, setTenantSlug] = useState<string>('');
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    acceptGdpr: false,
    acceptCookie: false,
    acceptNewsletter: false,
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceParam = searchParams.get('source');
  const source = sourceParam === 'qr' || sourceParam === 'nfc' || sourceParam === 'other'
    ? sourceParam
    : 'other';

  const buildRedirectWithSession = (redirectUrl: string, sessionId?: string): string => {
    if (!sessionId) return redirectUrl;
    const separator = redirectUrl.includes('?') ? '&' : '?';
    return `${redirectUrl}${separator}session_id=${encodeURIComponent(sessionId)}`;
  };

  // Check if user already has valid session
  // If a redirect param is present (QR flow), honor it; otherwise go to visitor-scanner
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const queryTenantId = searchParams.get('tenantId');
        const isTokenValid = payload.exp && payload.exp * 1000 > Date.now();
        const hasVisitorSession = payload.role === 'visitor' && !!payload.sessionId;
        const sameTenant = !queryTenantId || payload.tenantId === queryTenantId;

        // Auto-redirect only for active visitor sessions.
        // This avoids loops when an admin/superadmin token is present in localStorage.
        if (isTokenValid && hasVisitorSession && sameTenant) {
          const redirectParam = searchParams.get('redirect');
          if (redirectParam) {
            // QR flow: include session_id to let the gateway validate session and avoid redirect loops
            const nextUrl = buildRedirectWithSession(redirectParam, payload.sessionId);
            window.location.href = nextUrl;
          } else {
            // NFC / direct access: already logged in, go to scanner
            if (source === 'nfc') {
              axios.post('/events', {
                type: 'nfc_scanned',
                sessionId: payload.sessionId,
                data: {
                  source: 'nfc',
                  entryPoint: 'visitor_registration_existing_session',
                },
              }).catch(() => {
                // Non-blocking: tracking failures must not prevent access flow
              });
            }
            navigate('/visitor-scanner', { replace: true });
          }
        }
      } catch (e) {
        // Invalid token format, continue with registration
      }
    }
  }, [navigate, searchParams]);

  // Detect tenant from subdomain or ?tenantId query param
  useEffect(() => {
    const fetchTenantInfo = async () => {
      setError('');
      const hostname = window.location.hostname;
      const isIP = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

      const getTenantById = async (id: string) => {
        try {
          const response = await axios.get(`/platform/tenant/${encodeURIComponent(id)}`);
          if (response.data.success && response.data.data) {
            setTenantId(id);
            setTenantName(response.data.data.name || '');
            setTenantSlug(response.data.data.slug || '');
            return true;
          }
          setError(
            response.data.error?.message ||
              'Tenant not found. Please use the correct registration link.',
          );
          return false;
        } catch (error: any) {
          if (error.response?.status === 404) {
            setError('Tenant not found. Please use the correct registration link.');
            return false;
          }
          throw error;
        }
      };

      try {
        // Case 1: subdomain access (e.g. museo-test.local:5173)
        if (!isIP && !isLocalhost && hostname.includes('.')) {
          const subdomain = hostname.split('.')[0];
          try {
            const response = await axios.get('/platform/tenant-by-subdomain', {
              params: { subdomain },
            });
            if (response.data.success && response.data.data) {
              setTenantId(response.data.data.id);
              setTenantName(response.data.data.name);
              setTenantSlug(response.data.data.slug || '');
              return;
            }
          } catch (error: any) {
            if (error.response?.status !== 404) {
              throw error;
            }
          }
        }

        // Case 2: main host with ?tenantId
        const queryTenantId = searchParams.get('tenantId');
        const validQueryTenantId =
          queryTenantId &&
          queryTenantId !== 'null' &&
          queryTenantId !== 'undefined'
            ? queryTenantId
            : null;

        if (validQueryTenantId) {
          const loaded = await getTenantById(validQueryTenantId);
          if (!loaded) {
            return;
          }
          return;
        }

        setError('Missing tenant information. Please use the correct registration link.');
      } catch (err: any) {
        setError('Unable to load tenant information. Please try again.');
      }
    };

    fetchTenantInfo();
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!formData.acceptGdpr) {
        setError('You must accept the privacy policy');
        setIsLoading(false);
        return;
      }

      if (!formData.email || !formData.nome) {
        setError('Please fill in all required fields');
        setIsLoading(false);
        return;
      }

      if (!tenantId) {
        setError('Tenant information not loaded. Please refresh the page.');
        setIsLoading(false);
        return;
      }

      // Register visitor
      const response = await axios.post('/auth/register', {
        nome: formData.nome,
        email: formData.email,
        acceptGdpr: formData.acceptGdpr,
        acceptCookie: formData.acceptCookie,
        acceptNewsletter: formData.acceptNewsletter,
        source,
        tenantId: tenantId,
      });

      if (response.data.success) {
        // Store token in localStorage for session persistence
        if (response.data.data?.token) {
          localStorage.setItem('access_token', response.data.data.token);
        }

        // Track registration and consents
        trackUserRegistration(formData.email);
        if (formData.acceptCookie) trackConsentAcceptance('cookies');
        if (formData.acceptNewsletter) trackConsentAcceptance('newsletter');

        // Determine post-registration destination
        const redirectParam = searchParams.get('redirect');
        const satimapId = searchParams.get('satimapId') || '';

        if (redirectParam) {
          // QR flow: visitor scanned QR without session → go back to QR gateway
          // QR gateway will verify the new session and redirect to content with UTM params
          const nextUrl = buildRedirectWithSession(
            redirectParam,
            response.data.data?.sessionId,
          );
          window.location.href = nextUrl;
        } else {
          // NFC flow: visitor registered from NFC tag → go to visitor scanner with UTM params
          const slug = tenantSlug || tenantName.toLowerCase().replace(/\s+/g, '-');
          const utmParams = new URLSearchParams({
            utm_source: 'sati',
            utm_medium: source === 'nfc' ? 'nfc' : 'direct',
            utm_campaign: slug,
            ...(satimapId ? { utm_content: satimapId } : {}),
          });
          const scannerUrl = `/visitor-scanner${satimapId ? `?satimapId=${satimapId}&` : '?'}source=${encodeURIComponent(source)}&${utmParams.toString()}`;
          navigate(scannerUrl, { replace: true });
        }
      }
    } catch (err: any) {
      const message =
        err.response?.data?.error?.message ||
        (err.response?.data?.error?.errors?.[0]?.message) ||
        'Registration failed. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg text-center">
          <div className="mb-6 flex justify-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Registrazione completata!</h2>
          <p className="text-gray-600 mb-6">
            Benvenuto su {tenantName || 'il museo'}! Ora puoi iniziare a scansionare i codici QR delle attrazioni. La tua sessione è attiva per 24 ore.
          </p>
          <button
            onClick={() => {
              const slug = tenantSlug || tenantName.toLowerCase().replace(/\s+/g, '-');
              const satimapId = searchParams.get('satimapId') || '';
              const utmParams = new URLSearchParams({
                utm_source: 'sati',
                utm_medium: source === 'nfc' ? 'nfc' : 'direct',
                utm_campaign: slug,
                ...(satimapId ? { utm_content: satimapId } : {}),
              });
              navigate(`/visitor-scanner${satimapId ? `?satimapId=${satimapId}&` : '?'}source=${encodeURIComponent(source)}&${utmParams.toString()}`, { replace: true });
            }}
            className="w-full px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Scansiona QR Code
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg">
        {/* Titolo con icona mappa */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 leading-tight">
              Accedi alla<br />
              Mappa Immersiva di<br />
              {tenantName && <span className="text-indigo-600">{tenantName}</span>}
            </h1>
          </div>
          <svg opacity="0.6" viewBox="-7 -7 114.00 114.00" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" role="img" className="iconify iconify--gis h-20 w-20 flex-shrink-0" preserveAspectRatio="xMidYMid meet" fill="#3885d6" stroke="#3885d6" transform="rotate(0)" strokeWidth="2.8000000000000003"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M70.387 70l-3.854 7.247l18.87-3.085c-3.808-1.91-8.963-3.275-15.016-4.162zm-48.61 1.58C13.037 73.885 7.5 77.662 7.5 83.272a8.4 8.4 0 0 0 .774 3.497l30.285-4.95zM91.79 80l-42.15 6.87l11.116 12.646C79.01 97.881 92.5 92.05 92.5 83.272c0-1.17-.252-2.257-.71-3.271zm-49.272 8.055l-28.48 4.655C21.566 97.374 34.853 100 50 100c.918 0 1.815-.026 2.719-.045z" fill="#3885d6"></path><path d="M50.002 0c-16.3 0-29.674 13.333-29.674 29.596c0 6.252 1.987 12.076 5.342 16.865l19.234 33.25l.082.107c.759.991 1.5 1.773 2.37 2.348c.87.576 1.95.92 3.01.814c2.118-.212 3.415-1.708 4.646-3.376l.066-.086l21.234-36.141l.012-.023c.498-.9.866-1.816 1.178-2.708a29.246 29.246 0 0 0 2.17-11.05C79.672 13.333 66.302 0 50.002 0zm0 17.045c7.071 0 12.59 5.509 12.59 12.55c0 7.043-5.519 12.55-12.59 12.55c-7.072 0-12.594-5.508-12.594-12.55c0-7.04 5.523-12.55 12.594-12.55z"></path></g></svg>
        </div>
        
        <p className="text-gray-600 mb-4 text-sm">
          Registrati per esplorare luoghi, storie e contenuti interattivi del territorio.
        </p>
        
        <div className="mb-6 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-700 flex items-center gap-2">
          <svg 
            viewBox="0 0 16 16" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg" 
            height="20" 
            width="20"
            className="flex-shrink-0"
          >
            <desc>Timer Streamline Icon: https://streamlinehq.com</desc>
            <path fillRule="evenodd" clipRule="evenodd" d="M7.999978666666666 0.9066410666666666c-0.29456 0 -0.5333333333333333 0.2387776 -0.5333333333333333 0.5333376v2.3171839999999997c0 0.29454933333333333 0.2387733333333333 0.5333333333333333 0.5333333333333333 0.5333333333333333 0.29454933333333333 0 0.5333333333333333 -0.238784 0.5333333333333333 -0.5333333333333333V1.9965866666666665C11.611946666666666 2.266538666666667 14.026666666666667 4.8512640000000005 14.026666666666667 7.999978666666666c0 3.3284480000000003 -2.6982399999999997 6.026687999999999 -6.026687999999999 6.026687999999999 -3.3284373333333335 0 -6.026666666666667 -2.6982399999999997 -6.026666666666667 -6.026687999999999 0 -1.486784 0.5376960000000001 -2.846613333333333 1.4298773333333334 -3.8976853333333334 0.19061333333333333 -0.22455466666666668 0.16309333333333334 -0.5611200000000001 -0.061472 -0.7517333333333334 -0.22455466666666668 -0.19061333333333333 -0.5611200000000001 -0.16309333333333334 -0.7517333333333334 0.06146133333333333C1.5403200000000001 4.648618666666667 0.9066410666666666 6.250976 0.9066410666666666 7.999978666666666c0 3.9175679999999997 3.1757909333333334 7.0933546666666665 7.0933376 7.0933546666666665 3.9175679999999997 0 7.0933546666666665 -3.1757866666666668 7.0933546666666665 -7.0933546666666665 0 -3.9175466666666665 -3.1757866666666668 -7.0933376 -7.0933546666666665 -7.0933376ZM7.189856 8.619434666666667 4.5052053333333335 4.877194666666667c-0.07607466666666667 -0.10604799999999999 -0.06418133333333334 -0.2515733333333333 0.028106666666666665 -0.3438613333333333 0.09227733333333334 -0.092288 0.23781333333333335 -0.10418133333333333 0.34385066666666664 -0.028106666666666665l3.7422400000000002 2.6846506666666663c0.5136853333333333 0.368512 0.5742613333333333 1.10976 0.12724266666666667 1.5567893333333334 -0.44702933333333333 0.44702933333333333 -1.1882773333333332 0.38644266666666666 -1.5567893333333334 -0.12723199999999998Z" fill="#3333bd" strokeWidth="1.0667"></path>
          </svg>
          Ti bastano pochi secondi per iniziare l'esperienza.
        </div>

        {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">
              Nome e cognome *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <input
                id="nome"
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                placeholder="Inserisci il tuo nome completo"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Inserisci la tua email"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div className="space-y-3 border-t pt-4">
            <label className="flex items-start">
              <input
                type="checkbox"
                name="acceptGdpr"
                checked={formData.acceptGdpr}
                onChange={handleChange}
                className="mt-1"
                required
              />
              <span className="ml-3 text-sm text-gray-700">
                Accetto l&apos;
                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-indigo-600 hover:text-indigo-700 font-medium underline"
                >
                  informativa privacy e il trattamento dei dati
                </button>
                {' '}*
              </span>
            </label>

            <label className="flex items-start">
              <input
                type="checkbox"
                name="acceptCookie"
                checked={formData.acceptCookie}
                onChange={handleChange}
                className="mt-1"
              />
              <span className="ml-3 text-sm text-gray-700">Consenti i cookie per un&apos;esperienza migliore</span>
            </label>

            <label className="flex items-start">
              <input
                type="checkbox"
                name="acceptNewsletter"
                checked={formData.acceptNewsletter}
                onChange={handleChange}
                className="mt-1"
              />
              <span className="ml-3 text-sm text-gray-700">
                Desidero ricevere aggiornamenti ed eventi su {tenantName || 'questa esperienza'}
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full px-4 py-2 mt-6 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Registrazione in corso...' : 'Inizia l\'esperienza'}
          </button>
        </form>

      </div>

      <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} tenantId={tenantId || undefined} />
    </div>
  );
}